import { Button, buttonClasses, Callout, Dialog, DialogCancel, Input } from '@fruuit/ui'
import { useQueryClient } from '@tanstack/react-query'
import { ShieldCheck } from 'lucide-react'
import { type FormEvent, type ReactNode, useCallback, useRef, useState } from 'react'
import { Link } from 'react-router'
import { ApiError, errorMessage, isApiError } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { meQueryKey } from '../../api/session'
import { useSession } from '../../auth/session-context'
import { verifyMfa } from './api'

export interface StepUpDialogProps {
  open: boolean
  onVerified: () => void
  onClose: () => void
}

/**
 * Second-factor step-up (A-07, P-ID-012): a TOTP code or a recovery code marks this
 * session as MFA-verified, after which the privileged action is retried.
 */
export function StepUpDialog({ open, onVerified, onClose }: StepUpDialogProps) {
  const me = useSession()
  const queryClient = useQueryClient()
  const [code, setCode] = useState('')
  const verify = useApiMutation(verifyMfa, {
    onSuccess: async () => {
      setCode('')
      await queryClient.invalidateQueries({ queryKey: meQueryKey })
      onVerified()
    },
  })
  const enrolled = me.mfa.enrolled

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    verify.mutate({ code })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title="Confirm it's you"
      description={
        enrolled
          ? 'This action needs a second factor. Enter the 6-digit code from your authenticator app, or a recovery code.'
          : 'This action needs two-factor authentication, which is not set up on your account yet.'
      }
      footer={
        enrolled ? (
          <>
            <DialogCancel />
            <Button
              type="submit"
              form="step-up-form"
              loading={verify.isPending}
              icon={<ShieldCheck />}
            >
              Verify
            </Button>
          </>
        ) : (
          <>
            <DialogCancel>Not now</DialogCancel>
            <Link
              to="/account#security"
              className={buttonClasses({ variant: 'primary' })}
              onClick={onClose}
            >
              Set up two-factor authentication
            </Link>
          </>
        )
      }
    >
      {enrolled ? (
        <form id="step-up-form" onSubmit={onSubmit}>
          <Input
            label="Authentication code"
            name="code"
            autoComplete="one-time-code"
            required
            autoFocus
            value={code}
            onChange={(event) => setCode(event.target.value)}
            inputClassName="font-mono tracking-[0.2em]"
            error={verify.error ? errorMessage(verify.error) : undefined}
          />
        </form>
      ) : (
        <Callout tone="info">
          Set it up on your account page. It takes a minute with any authenticator app.
        </Callout>
      )}
    </Dialog>
  )
}

const STEP_UP_CANCELLED = new ApiError({
  status: 403,
  code: 'mfa_required',
  message: 'Confirm your identity with a second factor to continue.',
})

/**
 * Wrap privileged API calls: a 403 `mfa_required` opens the step-up dialog and, once
 * verified, retries the call once. Render `dialog` somewhere in the page.
 */
export function useStepUp(): {
  guard: <T>(task: () => Promise<T>) => Promise<T>
  dialog: ReactNode
} {
  const [open, setOpen] = useState(false)
  const waiter = useRef<{ resolve: () => void; reject: (error: unknown) => void } | null>(null)

  const guard = useCallback(async <T,>(task: () => Promise<T>): Promise<T> => {
    try {
      return await task()
    } catch (error) {
      if (!isApiError(error, 'mfa_required')) throw error
      await new Promise<void>((resolve, reject) => {
        waiter.current = { resolve, reject }
        setOpen(true)
      })
      return task()
    }
  }, [])

  const dialog = (
    <StepUpDialog
      open={open}
      onVerified={() => {
        setOpen(false)
        waiter.current?.resolve()
        waiter.current = null
      }}
      onClose={() => {
        setOpen(false)
        waiter.current?.reject(STEP_UP_CANCELLED)
        waiter.current = null
      }}
    />
  )
  return { guard, dialog }
}

/** Inline notice for a list that answered `mfa_required`; verifying refetches it. */
export function MfaRequiredNotice({ onVerified }: { onVerified: () => void }) {
  const [open, setOpen] = useState(false)
  return (
    <Callout tone="warning" title="Confirm it's you to continue" role="status">
      <p>Platform administration needs a second factor for this session.</p>
      <Button size="sm" className="mt-3" icon={<ShieldCheck />} onClick={() => setOpen(true)}>
        Verify now
      </Button>
      <StepUpDialog
        open={open}
        onClose={() => setOpen(false)}
        onVerified={() => {
          setOpen(false)
          onVerified()
        }}
      />
    </Callout>
  )
}
